import type {
  ContractStatus, DecisionStatus, HrDecision, KpiScore, LaborContract, LeaveRequest, OrgBlock, OrgDept, OrgSeat, Staff,
} from './types';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "22/Oct/2026" → "2026-10-22" (đúng dạng dữ liệu mẫu); không đọc được → null. */
export function isoOfBoardDate(s: string): string | null {
  const m = /^(\d{2})\/([A-Za-z]{3})\/(\d{4})/.exec(s);
  const mi = m ? MONTHS.indexOf(m[2]!) : -1;
  return m && mi >= 0 ? `${m[3]}-${String(mi + 1).padStart(2, '0')}-${m[1]}` : null;
}

/** Số người đang giữ ghế (nhóm gộp "8 tài xế" tính theo số đứng đầu). */
export function seatFill(seat: OrgSeat): number {
  return seat.holders.reduce((n, h) => n + Number(/^(\d+)\s/.exec(h.code)?.[1] ?? 1), 0);
}

export const seatVacancy = (seat: OrgSeat): number => Math.max(0, seat.plan - seatFill(seat));

/** Thực tế / định biên của một phòng. Người vượt định biên không làm số thực tế vượt. */
export function deptHeadcount(dept: OrgDept): { filled: number; plan: number } {
  return dept.seats.reduce((a, s) => ({ filled: a.filled + Math.min(seatFill(s), s.plan), plan: a.plan + s.plan }), { filled: 0, plan: 0 });
}

export function blockHeadcount(block: OrgBlock): { filled: number; plan: number; vacant: number } {
  const t = block.depts.map(deptHeadcount).reduce((a, d) => ({ filled: a.filled + d.filled, plan: a.plan + d.plan }), { filled: 0, plan: 0 });
  return { ...t, vacant: t.plan - t.filled };
}

/** Ghế chưa có ai giữ (cần leo lên cấp trên duyệt). */
export function vacantSeatCodes(org: readonly OrgBlock[]): string[] {
  return org.flatMap((b) => b.depts.flatMap((d) => d.seats.filter((s) => seatFill(s) === 0).map((s) => s.code)));
}

export const staffByDept = (staff: readonly Staff[], dept: string): Staff[] =>
  dept === 'Tất cả' ? [...staff] : staff.filter((s) => s.dept.split(' · ').includes(dept));

/** Chờ duyệt = trạng thái bắt đầu bằng "Chờ". */
export const isPendingLeave = (l: LeaveRequest): boolean => l.status.startsWith('Chờ') && l.tone === 'warn';

/** Trạng thái hiển thị của hợp đồng: Sắp hết hạn khi còn ≤ 30 ngày. */
export type ContractView = 'draft' | 'wait' | 'signed' | 'soon' | 'end';
export function contractView(c: LaborContract): ContractView {
  const map: Record<ContractStatus, ContractView | null> = { Draft: 'draft', Issued: 'wait', Terminated: 'end', Signed: null };
  return map[c.status] ?? (c.daysLeft !== null && c.daysLeft <= 30 ? 'soon' : 'signed');
}

export function contractCounts(cs: readonly LaborContract[]): Record<ContractView | 'all', number> {
  const r: Record<ContractView | 'all', number> = { all: cs.length, draft: 0, wait: 0, signed: 0, soon: 0, end: 0 };
  for (const c of cs) r[contractView(c)]++;
  return r;
}

/** Số lần đã ký HĐ xác định thời hạn của một người (Điều 20: tối đa 2 lần rồi phải không xác định thời hạn). */
export const fixedTermSigned = (cs: readonly LaborContract[], user: string): number =>
  Math.max(0, ...cs.filter((c) => c.user === user && c.kindLabel.startsWith('XĐTH')).map((c) => c.nth));

export function decisionCounts(ds: readonly HrDecision[]): Record<DecisionStatus, number> {
  const r: Record<DecisionStatus, number> = { Pending: 0, Approved: 0, Rejected: 0, Draft: 0 };
  for (const d of ds) r[d.status]++;
  return r;
}

/** Quyết định có tiền: người không có quyền lương thấy che. */
export const MASK_MONEY = '•••••• cần quyền lương';
export const decisionMasked = (d: HrDecision, types: ReadonlyArray<{ type: string; money: boolean }>, canMoney: boolean): boolean =>
  !canMoney && !!types.find((t) => t.type === d.type)?.money;

export type KpiBand = 'high' | 'mid' | 'low';
export const kpiBand = (k: KpiScore): KpiBand => (k.score >= 100 ? 'high' : k.score >= 90 ? 'mid' : 'low');
/** Chiều rộng thanh điểm KPI, % (trần 100, 120 điểm = đầy). */
export const kpiBarWidth = (k: KpiScore): number => Math.min(100, (k.score / 120) * 100);
